import {
  IrPieChartCard,
  IrPieChartFinData,
  IrPieChartsSimpleData, PieStyle,
} from "../../../../../investor-report/pojo/ir-common-classes";
import {
  FpValueService,
} from "../../../../../fund-performance/component/fund-performances/fund-performance-cards-wrapper/fp-cards-services/fp-modify-value.service";
import { FinCalc } from "../../../../../../services/financial-calculations/financial-calculations.service";
import { FundPerformance } from '../../../../../fund-performance/dto/FundPerformance';

export class PieChartSimpleCalculationsService {

  static prepareData(fp: FundPerformance): IrPieChartsSimpleData {
    const data = new IrPieChartsSimpleData();
    data.commitmentAllocation = this.getCommitmentAllocationData(fp);
    data.opexDrillDown = this.getOpexDrillDown(fp);
    return data;
  }


  public static calculatePieStyle(finData: IrPieChartFinData[]): PieStyle {
    const degrees = [];
    let startDeg = 0;
    let bgImage = '';

    finData.forEach((data) => {
      const valPcnt = Number(data.pcnt.slice(0, -1)) / 100; // transform ex. '12%' to number value 0.12
      const valDeg = Math.trunc(valPcnt * 360);
      degrees.push(startDeg + valDeg);
      startDeg += valDeg;
    });

    const degLength = degrees.length;

    degrees.forEach((deg, i) => {
      let degStr = ` ${deg}deg`;
      if (degLength === 1) {
        degStr = ` 0deg 0deg`;
      } else if (i !== 0) {
        degStr = ` ${degrees[i - 1]}deg ${deg}deg`;
      }
      if (i !== degLength - 1) {
        degStr += ',';
      }
      bgImage += finData[i].color + degStr;
    });

    bgImage = `conic-gradient(${bgImage})`;
    return { 'background-image': bgImage };
  }

  private static getCommitmentAllocationData(fp: FundPerformance): IrPieChartCard {
    if (!fp) {
      return null;
    }
    const opEx = fp.expectedOpEx ? (fp.totalCommitment * fp.expectedOpEx) : 0;
    const expectedFutureExpenses = (Math.abs(opEx) > Math.abs(fp.totalExpenses)) ? (opEx + fp.totalExpenses) : 0;
    const unAllocatedCommitment = fp.totalCommitment - Math.abs(fp.expectedFutureInv) - fp.totalCost
      - (Math.abs(opEx) < Math.abs(fp.totalExpenses) ? -fp.totalExpenses : opEx);
    let aboveCommitment = null;
    const finData: IrPieChartFinData[] = [];

    // 1. "Current investment" (Always added)
    finData.push({
      color: '#5E6F78',
      name: 'Current investment',
      value: FpValueService.modify(fp.totalCost, '', '', 2),
      pcnt: FpValueService.modify(FinCalc.ratioInPcnt(fp.totalCost, fp.totalCommitment), '', '%', 0),
    });

    // 2. "New investment allocation" (Added if `fp.expectedFutureInv` is present)
    if (fp.expectedFutureInv) {
      finData.push({
        color: '#41AFAB',
        name: 'New investment allocation',
        value: FpValueService.modify(fp.expectedFutureInv, '', '', 2),
        pcnt: FpValueService.modify(FinCalc.ratioInPcnt(fp.expectedFutureInv, fp.totalCommitment), '', '%', 0),
      });
    }

    // 3. "Current expenses" (Always added), should be shown always positive
    finData.push({
      color: '#FADCBB',
      name: 'Current expenses',
      value: FpValueService.modify(Math.abs(fp.totalExpenses), '', '', 2),
      pcnt: FpValueService.modify(FinCalc.ratioInPcnt(Math.abs(fp.totalExpenses), fp.totalCommitment), '', '%', 0),
    });

    // 4. "Expected future expenses" (Always added)
    finData.push({
      color: '#FBA74B',
      name: 'Expected future expenses',
      value: FpValueService.modify(expectedFutureExpenses, '', '', 2),
      pcnt: FpValueService.modify(FinCalc.ratioInPcnt(expectedFutureExpenses, fp.totalCommitment), '', '%', 0),
    });

    // 5. "Unallocated commitment to invest" (Added if `unAllocatedCommitment > 0`)
    if (unAllocatedCommitment > 0) {
      finData.push({
        color: '#92D2CF',
        name: 'Unallocated commitment to invest',
        value: FpValueService.modify(unAllocatedCommitment, '', '', 2),
        pcnt: FpValueService.modify(FinCalc.ratioInPcnt(unAllocatedCommitment, fp.totalCommitment), '', '%', 0),
      });
    }

    // Conditionally create "Above commitment" data if `unAllocatedCommitment < 0`
    if (unAllocatedCommitment < 0) {
      aboveCommitment = {
        color: '#92D2CF',
        name: 'Above commitment',
        value: FpValueService.modify(Math.abs(unAllocatedCommitment), '', '', 2),
        pcnt: FpValueService.modify(FinCalc.ratioInPcnt(Math.abs(unAllocatedCommitment), fp.totalCommitment), '', '%', 0),
      };
    }

    return {
      title: 'Commitment allocation',
      mainValue: FpValueService.modify(fp.totalCommitment, '', '', 2),
      finData: finData,
      pieStyle: this.calculatePieStyle(finData),
      aboveCommitment: aboveCommitment,
      emptyState: {
        empty: !fp.totalCommitment,
        title: 'No data yet',
        subtitle: 'Add total commitment to see chart.',
      },
    };
  }

  private static getOpexDrillDown(fp: FundPerformance): IrPieChartCard {
    if (!fp) {
      return null;
    }
    const other = (fp.mgmFees || fp.aggregatedOrgExpenses) ? (fp.totalExpenses - fp.mgmFees - fp.aggregatedOrgExpenses) : 0;
    const finData: IrPieChartFinData[] = [
      {
        color: '#5E6F78',
        name: 'Management fees',
        value: FpValueService.modify(Math.abs(fp.mgmFees), '', '', 2),
        pcnt: FpValueService.modify(FinCalc.ratioInPcnt(fp.mgmFees, fp.totalExpenses), '', '%', 0),
      },
      {
        color: '#FFDD2D',
        name: 'Organizational expenses',
        value: FpValueService.modify(Math.abs(fp.aggregatedOrgExpenses), '', '', 2),
        pcnt: FpValueService.modify(FinCalc.ratioInPcnt(fp.aggregatedOrgExpenses, fp.totalExpenses), '', '%', 0),
      },
      {
        color: '#41AFAB',
        name: 'Other',
        value: FpValueService.modify(Math.abs(other), '', '', 2),
        pcnt: FpValueService.modify(FinCalc.ratioInPcnt(other, fp.totalExpenses), '', '%', 0),
      },
    ];
    return {
      title: 'Expenses drilldown',
      mainValue: FpValueService.modify(Math.abs(fp.totalExpenses), '', '', 2),
      finData: finData,
      pieStyle: this.calculatePieStyle(finData),
      aboveCommitment: null,
      emptyState: {
        empty: !fp.totalExpenses || !fp.mgmFees || !fp.aggregatedOrgExpenses,
        title: 'No data yet',
        subtitle: 'Add OpEx to see the chart.',
      },
    };
  }
}

